import { motion } from "framer-motion";

const HERO_IMG = "/brand/hero-bg.jpg";

export const Hero = ({ onPrimaryClick, onSecondaryClick }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full min-h-[100vh] overflow-hidden bg-black aa-grain"
    >
      {/* Ambient background tints */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-amber-500/10 blur-[120px]" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-red-700/10 blur-[120px]" />
      </div>

      {/* Top corner labels */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 pt-28 md:pt-32">
        <div className="flex justify-between items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-neutral-400"
          >
            EST. 2026 — Las Vegas · Houston · San Francisco
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="hidden md:block text-[10px] uppercase tracking-[0.4em] text-amber-400/80"
          >
            VOL. 01 / 2026
          </motion.div>
        </div>
      </div>

      {/* Hero grid: text + image */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 mt-10 md:mt-12 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6 flex items-center gap-3"
            >
              <span className="w-8 h-px bg-amber-400" />
              Basketball Association
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.9, ease: "easeOut" }}
              data-testid="hero-title"
              className="font-display text-[14vw] md:text-[10vw] lg:text-[8.5rem] leading-[0.85] uppercase text-white"
            >
              AFRO<span className="aa-outline-text">AM</span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.9 }}
              className="font-display text-xl md:text-2xl lg:text-3xl leading-tight uppercase text-neutral-300 mt-6 tracking-wide"
            >
              <span className="inline-block">One Game.</span>{" "}
              <span className="inline-block text-amber-400">Two Cultures.</span>{" "}
              <span className="inline-block">One Family.</span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.65, duration: 0.8 }}
              data-testid="hero-tagline"
              className="text-base md:text-lg leading-relaxed text-neutral-300 max-w-xl mt-8 font-light"
            >
              A premier digital platform uniting African and American basketball
              cultures through elite competition, community mentorship, and
              cultural exchange.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.6 }}
              className="flex flex-wrap gap-4 mt-10"
            >
              <button
                onClick={onPrimaryClick}
                data-testid="hero-cta-primary"
                className="bg-amber-400 text-black px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
              >
                Join the Movement
              </button>
              <button
                onClick={onSecondaryClick}
                data-testid="hero-cta-secondary"
                className="border border-white text-white px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
              >
                Explore Programs
              </button>
            </motion.div>
          </div>

          {/* Right: brand image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 1, ease: "easeOut" }}
            className="lg:col-span-5 relative"
          >
            <div className="relative border border-amber-400/20">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-red-700/10 pointer-events-none" />
              <img
                src={HERO_IMG}
                alt="AfroAm Basketball Association — athletes united"
                className="w-full h-auto object-cover relative"
                data-testid="hero-image"
              />
              {/* Corner brackets */}
              <span className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-amber-400" />
              <span className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-amber-400" />
              <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-amber-400" />
              <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-amber-400" />
            </div>
            <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.35em] text-neutral-500">
              <span>The Movement</span>
              <span>2026 — Inaugural</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Tagline strip */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-amber-400/20 bg-black/70 backdrop-blur-sm">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center justify-center md:justify-between gap-3 text-[10px] md:text-xs uppercase tracking-[0.4em] text-neutral-300">
          <span className="text-white">One Game</span>
          <span className="text-amber-400">●</span>
          <span className="text-white">Two Cultures</span>
          <span className="text-amber-400">●</span>
          <span className="text-white">One Family</span>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="hidden md:flex absolute bottom-20 left-12 z-10 items-center gap-3 text-neutral-500">
        <div className="w-px h-12 bg-neutral-700" />
        <span className="text-[10px] uppercase tracking-[0.4em]">Scroll</span>
      </div>
    </section>
  );
};
